import React from 'react'
import { Button, Empty, Modal, Spin, Tabs } from 'antd'
import { useTranslation } from 'react-i18next'

import type { HistoryRecord } from '../../../types/history'
import { ImageResultView } from './ImageResultView'

type RecognitionComparisonMode = 'manual' | 'review'

interface RecognitionComparisonDetailProps {
    visible: boolean
    record: HistoryRecord | null
    loading?: boolean
    error?: string | null
    onClose: () => void
    onRetry?: () => void
    formatDate: (timestamp: number) => string
    comparisonMode?: RecognitionComparisonMode
    titleKey?: string
    primaryTabLabelKey?: string
    secondaryTabLabelKey?: string
    secondaryEmptyDescriptionKey?: string
    showSecondaryResult?: boolean
    rawClassLocalizedNames?: Record<string, string>
}

const noop = () => undefined

const DEFAULT_TITLE_KEY: Record<RecognitionComparisonMode, string> = {
    manual: 'modal.recognitionComparisonTitle',
    review: 'modal.detailTitle',
}

const DEFAULT_PRIMARY_TAB_LABEL_KEY: Record<RecognitionComparisonMode, string> =
    {
        manual: 'detail.modelRecognitionResult',
        review: 'detail.systemResult',
    }

const DEFAULT_SECONDARY_TAB_LABEL_KEY: Record<
    RecognitionComparisonMode,
    string
> = {
    manual: 'detail.manualRecognitionResult',
    review: 'detail.reviewResult',
}

const DEFAULT_SECONDARY_EMPTY_DESCRIPTION_KEY: Record<
    RecognitionComparisonMode,
    string
> = {
    manual: 'detail.manualRecognitionEmpty',
    review: 'messages.noDetection',
}

export const RecognitionComparisonDetail: React.FC<
    RecognitionComparisonDetailProps
> = ({
    visible,
    record,
    loading = false,
    error,
    onClose,
    onRetry,
    formatDate,
    comparisonMode = 'manual',
    titleKey,
    primaryTabLabelKey,
    secondaryTabLabelKey,
    secondaryEmptyDescriptionKey,
    showSecondaryResult = true,
    rawClassLocalizedNames,
}) => {
    const { t } = useTranslation('history')
    const resolvedTitleKey = titleKey ?? DEFAULT_TITLE_KEY[comparisonMode]
    const resolvedPrimaryTabLabelKey =
        primaryTabLabelKey ?? DEFAULT_PRIMARY_TAB_LABEL_KEY[comparisonMode]
    const resolvedSecondaryTabLabelKey =
        secondaryTabLabelKey ?? DEFAULT_SECONDARY_TAB_LABEL_KEY[comparisonMode]
    const resolvedSecondaryEmptyDescriptionKey =
        secondaryEmptyDescriptionKey ??
        DEFAULT_SECONDARY_EMPTY_DESCRIPTION_KEY[comparisonMode]

    const modelRecord = React.useMemo<HistoryRecord | null>(() => {
        if (!record) {
            return null
        }

        return {
            ...record,
            manual_recognition: null,
            review: null,
            effective_source: 'model',
            effective_predictions: Array.isArray(record.result.predictions)
                ? record.result.predictions
                : [],
            effective_annotated_image: record.result.annotated_image ?? null,
            result: {
                ...record.result,
                raw_predictions: undefined,
            },
        }
    }, [record])

    const manualRecord = React.useMemo<HistoryRecord | null>(() => {
        if (comparisonMode !== 'manual' || !record?.manual_recognition) {
            return null
        }

        return {
            ...record,
            effective_source: 'manual',
            effective_predictions: record.manual_recognition.predictions,
            effective_annotated_image:
                record.manual_recognition.annotated_image ?? null,
        }
    }, [comparisonMode, record])

    const reviewRecord = React.useMemo<HistoryRecord | null>(() => {
        if (
            comparisonMode !== 'review' ||
            !record?.review ||
            !Array.isArray(record.review.predictions) ||
            record.review.predictions.length === 0
        ) {
            return null
        }

        return {
            ...record,
            manual_recognition: null,
            review: null,
            effective_source: 'model',
            effective_predictions: Array.isArray(record.review.predictions)
                ? record.review.predictions
                : [],
            effective_annotated_image: record.result.annotated_image ?? null,
            result: {
                ...record.result,
                raw_predictions: undefined,
            },
        }
    }, [comparisonMode, record])

    const renderReadonlyResult = (
        targetRecord: HistoryRecord,
        readonlyPredictionSource: 'model' | 'effective',
        useManualRecognitionTypes: boolean
    ) => (
        <ImageResultView
            record={targetRecord}
            editable={false}
            annotationSeedPredictions={[]}
            annotationSeedKey={`${targetRecord.id}-${readonlyPredictionSource}`}
            annotationDirty={false}
            readonlyPredictionSource={readonlyPredictionSource}
            readonlyPreviewOnImageClick
            useManualRecognitionTypes={useManualRecognitionTypes}
            rawClassLocalizedNames={rawClassLocalizedNames}
            bypassCapabilityFallback
            onPredictionsChange={noop}
            onResetPredictions={noop}
            formatDate={formatDate}
        />
    )

    return (
        <Modal
            title={t(resolvedTitleKey)}
            open={visible}
            onCancel={onClose}
            width="72vw"
            style={{ top: 32, maxWidth: 1120 }}
            styles={{
                body: {
                    maxHeight: 'calc(100vh - 180px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                },
            }}
            footer={null}
            destroyOnClose
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: 48 }}>
                    <Spin size="large" />
                </div>
            ) : error ? (
                <Empty
                    description={
                        <div>
                            <div>{error}</div>
                            {onRetry ? (
                                <Button
                                    style={{ marginTop: 12 }}
                                    onClick={onRetry}
                                >
                                    {t('actions.refresh')}
                                </Button>
                            ) : null}
                        </div>
                    }
                />
            ) : record ? (
                <Tabs
                    items={[
                        {
                            key: 'model',
                            label: t(resolvedPrimaryTabLabelKey),
                            children: modelRecord
                                ? renderReadonlyResult(
                                      modelRecord,
                                      'effective',
                                      false
                                  )
                                : null,
                        },
                        ...(showSecondaryResult
                            ? [
                                  {
                                      key: comparisonMode,
                                      label: t(resolvedSecondaryTabLabelKey),
                                      children:
                                          comparisonMode === 'manual' ? (
                                              manualRecord ? (
                                                  renderReadonlyResult(
                                                      manualRecord,
                                                      'effective',
                                                      true
                                                  )
                                              ) : (
                                                  <div
                                                      style={{
                                                          padding: '56px 16px',
                                                      }}
                                                  >
                                                      <Empty
                                                          description={t(
                                                              resolvedSecondaryEmptyDescriptionKey
                                                          )}
                                                      />
                                                  </div>
                                              )
                                          ) : reviewRecord ? (
                                              renderReadonlyResult(
                                                  reviewRecord,
                                                  'effective',
                                                  false
                                              )
                                          ) : (
                                              <div
                                                  style={{
                                                      padding: '56px 16px',
                                                  }}
                                              >
                                                  <Empty
                                                      description={t(
                                                          resolvedSecondaryEmptyDescriptionKey
                                                      )}
                                                  />
                                              </div>
                                          ),
                                  },
                              ]
                            : []),
                    ]}
                />
            ) : (
                <Empty description={t('messages.noRecords')} />
            )}
        </Modal>
    )
}
